import { usePermissionsContext } from 'context/PermissionsContext'

/**
 * Hook para verificar se o usuário tem uma permissão específica
 * @param permission Permissão no formato "resource:action" (ex: "customers:create")
 * @returns true se o usuário tem a permissão
 */
export function usePermission(permission: string): boolean {
  const { hasPermission } = usePermissionsContext()
  return hasPermission(permission)
}

/**
 * Regra de acesso a uma seção inteira, com fallback para sessão legada.
 *
 * Lista de permissões vazia significa token sem o claim de RBAC (login
 * anterior ao SC-8642) ou contexto ainda não hidratado — não dá para concluir
 * que o usuário não tem acesso. Nesses casos libera, deixando o backend ser a
 * barreira real. É o mesmo fallback que o useRouteProtection já aplica.
 *
 * Use para esconder/bloquear uma seção inteira. Para botões de ação dentro de
 * uma seção já liberada, prefira <Can permission="..."> — ali negar por
 * engano é barato e reversível.
 */
export const canAccessSection = (
  permissions: string[],
  permission: string
): boolean => permissions.length === 0 || permissions.includes(permission)

export function useSectionPermission(permission: string): boolean {
  const { permissions } = usePermissionsContext()
  return canAccessSection(permissions, permission)
}

/**
 * Abas financeiras da ficha do cliente (cobranças, parcelamentos, recorrências,
 * notas fiscais e os cards da visão geral).
 *
 * Libera com a permissão do próprio módulo (quem vê todas as cobranças vê as de
 * um cliente) ou com `customers:view_financial`, que serve para quem não deve
 * ver o menu de Faturamento mas precisa do financeiro ao atender um cliente. O
 * backend aplica a mesma regra em Permissions::RoutePermissions::CUSTOMER_FINANCIAL.
 *
 * Mesmo fallback de sessão legada de `canAccessSection`.
 */
export const canViewCustomerFinancial = (
  permissions: string[],
  modulePermission: string
): boolean =>
  permissions.length === 0 ||
  permissions.includes(modulePermission) ||
  permissions.includes('customers:view_financial')

/**
 * Hook para verificar se o usuário tem pelo menos uma das permissões
 * @param permissions Lista de permissões no formato "resource:action"
 * @returns true se o usuário tem pelo menos uma das permissões
 */
export function useAnyPermission(permissions: string[]): boolean {
  const { hasAnyPermission } = usePermissionsContext()
  return hasAnyPermission(permissions)
}

/**
 * Hook para verificar se o usuário tem todas as permissões
 * @param permissions Lista de permissões no formato "resource:action"
 * @returns true se o usuário tem todas as permissões
 */
export function useAllPermissions(permissions: string[]): boolean {
  const { hasAllPermissions } = usePermissionsContext()
  return hasAllPermissions(permissions)
}

/**
 * Hook para obter todas as permissões do usuário
 * @returns Lista de permissões do usuário
 */
export function useUserPermissions(): string[] {
  const { permissions } = usePermissionsContext()
  return permissions
}
